import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
    Bars3Icon,
    XMarkIcon,
    MagnifyingGlassIcon,
    ChevronDownIcon,
    AcademicCapIcon,
    BuildingLibraryIcon,
    ChevronRightIcon,
    ClockIcon,
    UserGroupIcon,
    VideoCameraIcon
} from '@heroicons/react/24/outline'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { degreeAPI, universityAPI, courseAPI, approvalAPI, specializationAPI } from '../../services/api'
import SearchModal from '../Search/SearchModal'
import EnquiryModal from '../Enquiry/EnquiryModal'

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isScrolled, setIsScrolled] = useState(false)
    const [isSearchOpen, setIsSearchOpen] = useState(false)
    const [isEnquiryOpen, setIsEnquiryOpen] = useState(false)
    const [enquiryType, setEnquiryType] = useState('consultation')
    const [activeFlyout, setActiveFlyout] = useState(null)
    const [activeCourseLevel, setActiveCourseLevel] = useState('Undergraduate (UG)')
    const [activeUniversityDegree, setActiveUniversityDegree] = useState(null)
    const location = useLocation()

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10)
        }

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        setIsMenuOpen(false)
        setActiveFlyout(null)
    }, [location])

    // Fetch data for mega menus
    const { data: degreesData } = useQuery({
        queryKey: ['degrees'],
        queryFn: () => degreeAPI.getDegrees(),
        staleTime: 1000 * 60 * 5 // 5 minutes
    })

    const { data: levelsData } = useQuery({
        queryKey: ['levels'],
        queryFn: () => courseAPI.getLevels(),
        staleTime: 1000 * 60 * 5
    })

    const { data: approvalsData } = useQuery({
        queryKey: ['approvals'],
        queryFn: () => approvalAPI.getApprovals(),
        staleTime: 1000 * 60 * 5
    })

    const { data: specializationsData } = useQuery({
        queryKey: ['specializations', activeUniversityDegree],
        queryFn: () => specializationAPI.getSpecializations({ degree: activeUniversityDegree }),
        enabled: !!activeUniversityDegree,
        staleTime: 1000 * 60 * 5
    })

    const levels = levelsData?.data?.levels || ['Undergraduate (UG)', 'Postgraduate (PG)', 'Diploma', 'Certificate']
    const degrees = degreesData?.data?.degrees || []
    const approvals = approvalsData?.data?.approvals || []
    const specializations = specializationsData?.data?.specializations || []

    const filteredDegrees = degrees.filter(d => d.level === activeCourseLevel)

    // Set initial active university degree when degrees load
    useEffect(() => {
        if (degrees.length > 0 && !activeUniversityDegree) {
            setActiveUniversityDegree(degrees[0]._id)
        }
    }, [degrees, activeUniversityDegree])

    const navigation = [
        { name: 'Home', href: '/' },
        {
            name: 'Courses',
            href: '/courses',
            hasMegaMenu: true,
            type: 'courses'
        },
        {
            name: 'Universities',
            href: '/universities',
            hasMegaMenu: true,
            type: 'universities'
        },
        { name: 'Blogs', href: '/blogs' },
        { name: 'Contact', href: '/contact' },
    ]

    return (
        <>
            <header
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
                    ? 'bg-white shadow-lg border-b border-gray-200'
                    : 'bg-white/90 backdrop-blur-sm'
                    }`}
                onMouseLeave={() => setActiveFlyout(null)}
            >
                <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16 lg:h-20">
                        {/* Logo */}
                        <Link to="/" className="flex items-center space-x-2">
                            <img
                                src="/marglogo.png"
                                alt="Margdarshak Logo"
                                className="h-10 sm:h-12 object-contain"
                            />
                        </Link>

                        {/* Desktop Navigation */}
                        <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
                            {navigation.map((item) => (
                                <div
                                    key={item.name}
                                    className="relative h-full flex items-center"
                                    onMouseEnter={() => item.hasMegaMenu ? setActiveFlyout(item.type) : setActiveFlyout(null)}
                                >
                                    <Link
                                        to={item.href}
                                        className={`group flex items-center text-sm font-semibold transition-colors duration-200 hover:text-accent-600 ${location.pathname === item.href
                                            ? 'text-accent-600'
                                            : 'text-gray-700'
                                            }`}
                                    >
                                        <span>{item.name}</span>
                                        {item.hasMegaMenu && (
                                            <ChevronDownIcon className={`ml-1 w-4 h-4 transition-transform duration-200 ${activeFlyout === item.type ? 'rotate-180 text-accent-600' : ''}`} />
                                        )}
                                        {/* Underline effect */}
                                        <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-accent-600 transform origin-left transition-transform duration-300 ${location.pathname === item.href || activeFlyout === item.type ? 'scale-x-100' : 'scale-x-0'}`}></span>
                                    </Link>
                                </div>
                            ))}
                        </div>

                        {/* Desktop Actions */}
                        <div className="hidden lg:flex items-center space-x-4">
                            <button
                                onClick={() => setIsSearchOpen(true)}
                                className="p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-colors"
                                title="Search"
                            >
                                <MagnifyingGlassIcon className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => {
                                    setEnquiryType('consultation')
                                    setIsEnquiryOpen(true)
                                }}
                                className="bg-accent-600 text-white px-5 py-2.5 rounded-full text-sm font-bold hover:bg-accent-700 shadow-md hover:shadow-lg transition-all"
                            >
                                Free Counselling
                            </button>
                        </div>

                        {/* Mobile menu button */}
                        <div className="lg:hidden flex items-center space-x-2">
                            <button
                                onClick={() => setIsSearchOpen(true)}
                                className="p-2 text-gray-600"
                            >
                                <MagnifyingGlassIcon className="w-6 h-6" />
                            </button>
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="p-2 text-gray-600"
                            >
                                {isMenuOpen ? <XMarkIcon className="w-7 h-7" /> : <Bars3Icon className="w-7 h-7" />}
                            </button>
                        </div>
                    </div>
                </nav>

                {/* Mega Menus */}
                <AnimatePresence>
                    {activeFlyout === 'courses' && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="hidden lg:block absolute top-full left-0 w-full bg-white shadow-2xl border-t border-gray-100 overflow-hidden"
                            style={{ minHeight: '400px' }}
                        >
                            <div className="container mx-auto flex h-full min-h-[400px]">
                                {/* Sidebar Categories */}
                                <div className="w-1/4 bg-gray-50 p-6 border-r border-gray-100">
                                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Domains</h3>
                                    <div className="space-y-1">
                                        {levels.map((level) => (
                                            <button
                                                key={level}
                                                onMouseEnter={() => setActiveCourseLevel(level)}
                                                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeCourseLevel === level
                                                    ? 'bg-primary-600 text-white shadow-lg translate-x-1'
                                                    : 'text-gray-700 hover:bg-white hover:shadow-md hover:text-primary-600'
                                                    }`}
                                            >
                                                <div className="flex items-center">
                                                    <AcademicCapIcon className={`w-5 h-5 mr-3 ${activeCourseLevel === level ? 'text-white' : 'text-primary-500'}`} />
                                                    <span>{level.replace(' (UG)', '').replace(' (PG)', '')} Programs</span>
                                                </div>
                                                <ChevronRightIcon className={`w-4 h-4 ${activeCourseLevel === level ? 'opacity-100' : 'opacity-0'}`} />
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Content Grid */}
                                <div className="w-3/4 p-8 bg-white">
                                    <div className="flex items-center justify-between mb-8">
                                        <h3 className="text-2xl font-bold text-gray-900 border-l-4 border-accent-500 pl-4">Available {activeCourseLevel.replace(' (UG)', '').replace(' (PG)', '')} Courses</h3>
                                        <Link to="/courses" className="text-accent-600 font-bold hover:underline text-sm flex items-center">
                                            View All <ChevronRightIcon className="w-4 h-4 ml-1" />
                                        </Link>
                                    </div>
                                    <div className="grid grid-cols-3 gap-6">
                                        {filteredDegrees.length > 0 ? (
                                            filteredDegrees.slice(0, 9).map((degree) => (
                                                <Link
                                                    key={degree._id}
                                                    to={`/${activeFlyout === 'courses' ? 'courses' : 'universities'}?level=${encodeURIComponent(activeCourseLevel)}&degrees=${degree._id}`}
                                                    className="group p-4 rounded-xl border border-gray-100 hover:border-accent-100 hover:bg-accent-50 transition-all duration-300 flex items-start space-x-4 shadow-sm hover:shadow-md"
                                                >
                                                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center text-accent-600 group-hover:bg-accent-600 group-hover:text-white transition-colors">
                                                        <AcademicCapIcon className="w-6 h-6" />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-gray-900 group-hover:text-accent-700">{degree.name}</h4>
                                                        <p className="text-xs text-gray-500 mt-1 line-clamp-1">{degree.duration || '2-3 Years'}</p>
                                                    </div>
                                                </Link>
                                            ))
                                        ) : (
                                            <div className="col-span-3 text-center py-12 text-gray-400 font-medium">No courses listed for this level.</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {activeFlyout === 'universities' && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="hidden lg:block absolute top-full left-0 w-full bg-white shadow-2xl border-t border-gray-100 overflow-hidden"
                        >
                            <div className="container mx-auto">
                                <div className="flex min-h-[500px]">
                                    {/* Sidebar: Degrees */}
                                    <div className="w-1/4 bg-gray-50 p-8 border-r border-gray-100 overflow-y-auto max-h-[600px]">
                                        <div className="mb-6 flex items-center justify-between">
                                            <h3 className="text-xl font-extrabold text-gray-900 tracking-tight">Select Degree</h3>
                                            <BuildingLibraryIcon className="w-5 h-5 text-primary-600" />
                                        </div>
                                        <div className="space-y-2">
                                            {degrees.map((degree) => (
                                                <button
                                                    key={degree._id}
                                                    onMouseEnter={() => setActiveUniversityDegree(degree._id)}
                                                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeUniversityDegree === degree._id
                                                        ? 'bg-primary-600 text-white shadow-lg translate-x-1'
                                                        : 'text-gray-700 hover:bg-white hover:shadow-md hover:text-primary-600'
                                                        }`}
                                                >
                                                    <div className="flex items-center">
                                                        <AcademicCapIcon className={`w-5 h-5 mr-3 ${activeUniversityDegree === degree._id ? 'text-white' : 'text-primary-400'}`} />
                                                        <span className="truncate">{degree.name}</span>
                                                    </div>
                                                    <ChevronRightIcon className={`w-4 h-4 transition-transform ${activeUniversityDegree === degree._id ? 'rotate-0' : '-rotate-90 opacity-0 group-hover:opacity-100'}`} />
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Content: Specializations */}
                                    <div className="w-3/4 p-10 bg-white overflow-y-auto max-h-[600px]">
                                        <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-50">
                                            <div>
                                                <span className="text-accent-600 text-xs font-black uppercase tracking-widest mb-1 block">Specializations for</span>
                                                <h3 className="text-3xl font-black text-gray-900 leading-none">
                                                    {degrees.find(d => d._id === activeUniversityDegree)?.name || 'Degree'}
                                                </h3>
                                            </div>
                                            <Link
                                                to={`/universities?degrees=${activeUniversityDegree}`}
                                                className="bg-primary-50 text-primary-700 px-5 py-2 rounded-full text-sm font-bold hover:bg-primary-100 transition-colors flex items-center"
                                            >
                                                Explore All {degrees.find(d => d._id === activeUniversityDegree)?.shortName || ''} Universities
                                                <ChevronRightIcon className="w-4 h-4 ml-2" />
                                            </Link>
                                        </div>

                                        {specializations.length > 0 ? (
                                            <div className="grid grid-cols-2 gap-6">
                                                {specializations.map((spec) => (
                                                    <Link
                                                        key={spec._id}
                                                        to={`/universities?degrees=${activeUniversityDegree}&specializations=${spec._id}`}
                                                        className="group p-5 rounded-2xl border border-gray-100 hover:border-accent-100 hover:bg-accent-50 transition-all duration-300 flex items-start space-x-4 shadow-sm hover:shadow-md"
                                                    >
                                                        <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center text-accent-600 group-hover:bg-accent-600 group-hover:text-white transition-colors flex-shrink-0 shadow-inner">
                                                            <AcademicCapIcon className="w-6 h-6" />
                                                        </div>
                                                        <div>
                                                            <h4 className="font-bold text-gray-900 group-hover:text-accent-700 transition-colors line-clamp-1">{spec.name}</h4>
                                                            <p className="text-xs text-gray-500 mt-1 line-clamp-2 leading-relaxed">Top universities offering {spec.name}</p>
                                                            <div className="flex items-center mt-3 text-accent-600 text-xs font-bold opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all">
                                                                View Universities <ChevronRightIcon className="w-3 h-3 ml-1" />
                                                            </div>
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center h-64 text-center">
                                                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                                    <MagnifyingGlassIcon className="w-10 h-10 text-gray-300" />
                                                </div>
                                                <h4 className="text-xl font-bold text-gray-800">No Specializations Found</h4>
                                                <p className="text-gray-500 max-w-xs mt-2">We couldn't find any specific specializations for this degree yet. Check back soon!</p>
                                            </div>
                                        )}

                                        {/* Bottom Highlight */}
                                        <div className="mt-10 p-6 rounded-2xl bg-gradient-to-r from-accent-600 to-orange-500 text-white flex items-center justify-between shadow-lg">
                                            <div className="flex items-center">
                                                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-4">
                                                    <VideoCameraIcon className="w-6 h-6" />
                                                </div>
                                                <div>
                                                    <h5 className="font-bold">Need help choosing?</h5>
                                                    <p className="text-accent-50 text-sm">Talk to our experts for a free career counseling session.</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setEnquiryType('consultation')
                                                    setIsEnquiryOpen(true)
                                                }}
                                                className="bg-white text-accent-600 px-6 py-2.5 rounded-full font-bold text-sm hover:shadow-xl transition-all hover:scale-105"
                                            >
                                                Book Free Session
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Mobile Navigation */}
                <AnimatePresence>
                    {isMenuOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="lg:hidden bg-white border-t border-gray-200 shadow-xl"
                        >
                            <div className="px-6 py-8 space-y-6">
                                {navigation.map((item) => (
                                    <Link
                                        key={item.name}
                                        to={item.href}
                                        className={`block text-xl font-bold transition-colors duration-200 ${location.pathname === item.href
                                            ? 'text-accent-600'
                                            : 'text-gray-800 hover:text-accent-600'
                                            }`}
                                    >
                                        {item.name}
                                    </Link>
                                ))}
                                <div className="pt-6 border-t border-gray-100 space-y-4">
                                    <button
                                        onClick={() => {
                                            setEnquiryType('consultation')
                                            setIsEnquiryOpen(true)
                                        }}
                                        className="w-full bg-accent-600 text-white py-4 rounded-2xl font-bold text-lg shadow-lg"
                                    >
                                        Free Counselling
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </header>

            {/* Search Modal */}
            <SearchModal
                isOpen={isSearchOpen}
                onClose={() => setIsSearchOpen(false)}
            />

            {/* Enquiry Modal */}
            <EnquiryModal
                isOpen={isEnquiryOpen}
                onClose={() => setIsEnquiryOpen(false)}
                enquiryType={enquiryType}
            />
        </>
    )
}

export default Header
